import React, { useState, useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ContactButtons = forwardRef(function ContactButtons({ therapist, onContactClick, className = "", size = "default" }, ref) {
  const [showContactModal, setShowContactModal] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionDelay, setSubmissionDelay] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    preferredContact: 'email',
    // Honeypot fields (hidden from users)
    website: '', // Bots often fill this
    honeypot: '', // Another honeypot
    comment: '' // Third honeypot
  });
  
  const formStartTime = useRef(null);
  const interactionCount = useRef(0);
  const lastSubmissionTime = useRef(0);

  // Anti-spam configuration - SIMPLIFIED
  const SPAM_CONFIG = {
    MIN_FORM_TIME: 2000, // Minimum 2 seconds (very lenient)
    MAX_FORM_TIME: 3600000, // Maximum 1 hour (very generous)
    MIN_MESSAGE_WORDS: 2, // Just 2 words minimum
    MAX_SUBMISSION_RATE: 10, // Max 10 submissions per hour (aligned with backend)
    FORBIDDEN_PATTERNS: [
      // Only the most obvious spam patterns
      /(https?:\/\/[^\s]+){3,}/i, // 3+ URLs
      /\b\d{4}[-\s]\d{4}[-\s]\d{4}[-\s]\d{4}\b/, // Credit card numbers
    ]
  };

  // Initialize form timing when modal opens
  useEffect(() => {
    if (showContactModal) {
      formStartTime.current = Date.now();
      interactionCount.current = 0;
    }
  }, [showContactModal]);

  // Rate limiting with localStorage - SIMPLIFIED
  const checkRateLimit = () => {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    const storageKey = 'contactSubmissions';
    
    try {
      let submissions = JSON.parse(localStorage.getItem(storageKey) || '[]');
      
      // Clean old submissions
      submissions = submissions.filter(timestamp => now - timestamp < oneHour);
      
      // Check if rate limit exceeded
      if (submissions.length >= SPAM_CONFIG.MAX_SUBMISSION_RATE) {
        const oldestSubmission = Math.min(...submissions);
        const timeUntilNext = oneHour - (now - oldestSubmission);
        return {
          allowed: false,
          waitTime: Math.ceil(timeUntilNext / 60000) // Minutes
        };
      }
      
      return { allowed: true };
    } catch (error) {
      console.warn('Rate limiting storage error:', error);
      return { allowed: true }; // Allow if localStorage fails
    }
  };

  // Add submission to rate limit tracking
  const recordSubmission = () => {
    try {
      const now = Date.now();
      const storageKey = 'contactSubmissions';
      let submissions = JSON.parse(localStorage.getItem(storageKey) || '[]');
      submissions.push(now);
      localStorage.setItem(storageKey, JSON.stringify(submissions));
      lastSubmissionTime.current = now;
    } catch (error) {
      console.warn('Could not record submission for rate limiting:', error);
    }
  };

  // Content analysis for spam detection - SIMPLIFIED
  const analyzeContent = (text) => {
    const issues = [];
    
    // Only check for the most obvious spam patterns
    SPAM_CONFIG.FORBIDDEN_PATTERNS.forEach((pattern, index) => {
      if (pattern.test(text)) {
        issues.push(`Suspicious content detected`);
      }
    });
    
    // Very basic word count check
    const wordCount = text.trim().split(/\s+/).length;
    if (wordCount < SPAM_CONFIG.MIN_MESSAGE_WORDS) {
      issues.push('Message too short - please provide more details');
    }
    
    return issues;
  };

  // Simplified form validation with basic spam detection
  const validateContactForm = () => {
    const errors = {};
    
    // Check honeypot fields (should be empty)
    if (contactForm.website || contactForm.honeypot || contactForm.comment) {
      errors.spam = 'Spam detected - submission blocked';
      return errors;
    }
    
    // Very lenient form timing check
    const now = Date.now();
    const formDuration = now - (formStartTime.current || now);
    
    if (formDuration < SPAM_CONFIG.MIN_FORM_TIME) {
      errors.timing = `Please take a moment before submitting`;
    }
    
    // Rate limiting check
    const rateCheck = checkRateLimit();
    if (!rateCheck.allowed) {
      errors.rateLimit = `Too many submissions. Please wait ${rateCheck.waitTime} minutes before trying again.`;
    }
    
    // Basic field validation - SIMPLIFIED
    if (!contactForm.name.trim() || contactForm.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    } else if (contactForm.name.trim().length > 100) {
      errors.name = 'Name cannot exceed 100 characters';
    }
    
    // Email validation
    if (!contactForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Phone validation (if provided)
    if (contactForm.phone && !/^[+]?[(]?[\d\s\-().]{10,}$/.test(contactForm.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    // Message validation - SIMPLIFIED
    if (!contactForm.message.trim()) {
      errors.message = 'Message is required';
    } else if (contactForm.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    } else if (contactForm.message.trim().length > 2000) {
      errors.message = 'Message cannot exceed 2000 characters';
    } else {
      const contentIssues = analyzeContent(contactForm.message);
      if (contentIssues.length > 0) {
        errors.message = contentIssues[0]; // Show first issue
      }
    }
    
    return errors;
  };

  // Calculate submission delay - SIMPLIFIED
  const calculateSubmissionDelay = () => {
    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime.current;
    
    // Only add delay for very rapid submissions
    if (timeSinceLastSubmission < 10000) { // Less than 10 seconds
      return 2000; // 2 second delay
    }
    
    return 0; // No delay
  };

  // Track contact interaction securely (HIPAA-compliant - no PHI stored)
  const trackContactInteraction = async (contactMethod, additionalData = {}) => {
    if (isTracking) return; // Prevent duplicate tracking
    setIsTracking(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      await fetch(`${API_BASE_URL}/api/analytics/contact-interaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          therapistId: therapist._id,
          contactMethod, // 'message', 'phone', 'website'
          timestamp: new Date().toISOString(),
          referrer: document.referrer || '',
          userAgent: navigator.userAgent,
          ...additionalData
        }),
      });
    } catch (error) {
      console.error('Contact tracking error:', error);
      // Don't block user interaction if tracking fails
    } finally {
      setIsTracking(false);
    }
  };

  // Handle message contact (existing modal system)
  const handleMessageContact = () => {
    setShowContactModal(true);
    setTimeout(() => setIsAnimating(true), 10); // Small delay to trigger animation
    if (onContactClick) onContactClick();
  };

  // Handle closing with animation
  const handleCloseModal = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setShowContactModal(false);
      setIsAnimating(false);
      setFormErrors({});
      setSubmissionDelay(0);
    }, 300); // Wait for animation to complete
  };

  // Handle phone contact with click-to-call
  const handlePhoneContact = (phoneNumber) => {
    if (!phoneNumber) return;

    trackContactInteraction('phone', {
      phoneNumber: 'REDACTED' // Don't log actual phone number for privacy
    });

    // Create click-to-call link
    const telLink = `tel:${phoneNumber.replace(/[^\d+]/g, '')}`;
    window.location.href = telLink;
  };

  // Handle website visit with referral tracking
  const handleWebsiteVisit = (websiteUrl) => {
    if (!websiteUrl) return;

    // Ensure URL has protocol
    let formattedUrl = websiteUrl;
    if (!websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
      formattedUrl = `https://${websiteUrl}`;
    }

    try {
      // Add referral parameters for tracking
      const url = new URL(formattedUrl);
      url.searchParams.set('ref', 'therapylist');
      url.searchParams.set('therapist_id', therapist._id);
      url.searchParams.set('source', 'profile_page');
      url.searchParams.set('utm_source', 'therapylist');
      url.searchParams.set('utm_medium', 'referral');
      url.searchParams.set('utm_campaign', 'therapist_profile');

      trackContactInteraction('website', {
        originalUrl: websiteUrl,
        trackedUrl: url.toString()
      });

      // Open in new tab
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Invalid website URL:', error);
      // Fallback: try opening the original URL
      window.open(formattedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Handle contact form changes with interaction tracking
  const handleContactFormChange = (e) => {
    const { name, value } = e.target;
    
    // Track user interaction (for timing analysis)
    interactionCount.current += 1;
    
    // Clear errors for this field
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  // Enhanced contact form submission with anti-spam
  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return; // Prevent double submission
    
    // Validate form with spam detection
    const validationErrors = validateContactForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      
      // Show first error in alert for critical issues
      if (validationErrors.spam || validationErrors.rateLimit || validationErrors.timing) {
        alert(Object.values(validationErrors)[0]);
      }
      return;
    }
    
    // Calculate and apply submission delay
    const delay = calculateSubmissionDelay();
    if (delay > 0) {
      setSubmissionDelay(delay);
      setIsSubmitting(true);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      
      setSubmissionDelay(0);
    } else {
      setIsSubmitting(true);
    }
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      // Prepare sanitized data (exclude honeypot fields)
      const sanitizedData = {
        therapistId: therapist._id,
        clientInfo: {
          name: contactForm.name.trim(),
          email: contactForm.email.trim().toLowerCase(),
          phone: contactForm.phone.trim()
        },
        inquiryDetails: {
          message: contactForm.message.trim(),
          preferredContact: contactForm.preferredContact
        },
        // Anti-spam metadata
        metadata: {
          formDuration: Date.now() - formStartTime.current,
          interactionCount: interactionCount.current,
          timestamp: new Date().toISOString()
        }
      };

      const response = await fetch(`${API_BASE_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sanitizedData),
      });

      if (response.ok) {
        // Record successful submission for rate limiting
        recordSubmission();
        
        // Track successful message submission
        trackContactInteraction('message');
        
        // Show success popup instead of alert
        setShowSuccessPopup(true);
        handleCloseModal();
        setContactForm({ 
          name: '', 
          email: '', 
          phone: '', 
          message: '', 
          preferredContact: 'email',
          website: '',
          honeypot: '',
          comment: ''
        });
      } else {
        const errorData = await response.json();
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const errorMessages = errorData.errors.map(err => err.msg).join('\n');
          alert(`Please fix the following errors:\n${errorMessages}`);
        } else {
          alert(errorData.message || 'Failed to send message. Please try again.');
        }
      }
    } catch (err) {
      console.error('Contact form error:', err);
      alert('There was an error sending your message. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format phone number for display (partial masking for security)
  const formatPhoneForDisplay = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/[^\d]/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  // Check if therapist has contact methods available
  const hasPhone = therapist.phone && therapist.phone.trim();
  const hasWebsite = therapist.website && therapist.website.trim();

  // Button size variants
  const buttonSizes = {
    default: {
      primary: "w-full bg-[#254e70] text-white py-4 px-4 rounded-xl font-semibold hover:bg-[#1d3e5a] hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center gap-2",
      secondary: "flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-xl font-medium transition-all duration-200 text-sm",
      iconSize: "w-5 h-5"
    },
    compact: {
      primary: "w-full bg-[#254e70] text-white py-2.5 px-3 rounded-lg font-semibold hover:bg-[#1d3e5a] hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center gap-1.5",
      secondary: "flex items-center justify-center gap-1.5 px-3 py-2 border-2 rounded-lg font-medium transition-all duration-200 text-sm",
      iconSize: "w-4 h-4"
    }
  };

  const currentSizes = buttonSizes[size] || buttonSizes.default;

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openModal: () => {
      setShowContactModal(true);
      setTimeout(() => setIsAnimating(true), 10);
      if (onContactClick) onContactClick();
    },
    closeModal: handleCloseModal
  }));

  return (
    <>
      <div className={`space-y-3 ${className}`}>
        {/* Primary Contact Button - Send Message */}
        <button
          onClick={handleMessageContact}
          className={currentSizes.primary}
          style={{ fontFamily: 'Nunito Sans, sans-serif' }}
        >
          <svg className={currentSizes.iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Send Message to {therapist.firstName}
        </button>

        {/* Secondary Contact Methods */}
        <div className="grid grid-cols-2 gap-3">
          {/* Phone Button */}
          {hasPhone ? (
            <button
              onClick={() => handlePhoneContact(therapist.phone)}
              className={`${currentSizes.secondary} border-[#9dacff] text-[#9dacff] hover:bg-[#9dacff] hover:text-white`}
              style={{ fontFamily: 'Nunito Sans, sans-serif' }}
              title={`Call ${formatPhoneForDisplay(therapist.phone)}`}
            >
              <svg className={currentSizes.iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="hidden sm:inline">Call</span>
              <span className="sm:hidden">Call</span>
            </button>
          ) : (
            <div className={`${currentSizes.secondary} border-gray-200 text-gray-400`}>
              <svg className={`${currentSizes.iconSize} mr-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="hidden sm:inline">No Phone</span>
              <span className="sm:hidden">No Phone</span>
            </div>
          )}

          {/* Website Button */}
          {hasWebsite ? (
            <button
              onClick={() => handleWebsiteVisit(therapist.website)}
              className={`${currentSizes.secondary} border-[#254e70] text-[#254e70] hover:bg-[#254e70] hover:text-white`}
              style={{ fontFamily: 'Nunito Sans, sans-serif' }}
            >
              <svg className={currentSizes.iconSize} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span className="hidden sm:inline">Website</span>
              <span className="sm:hidden">Visit Website</span>
            </button>
          ) : (
            <div className={`${currentSizes.secondary} border-gray-200 text-gray-400`}>
              <svg className={`${currentSizes.iconSize} mr-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span className="hidden sm:inline">No Website</span>
              <span className="sm:hidden">No Website</span>
            </div>
          )}
        </div>


      </div>

      {/* Enhanced Contact Slide-Out Panel */}
      {showContactModal && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}
            onClick={handleCloseModal}
          />
          
          {/* Slide-out Panel */}
          <div className={`fixed top-0 right-0 h-full w-full sm:w-[480px] bg-white shadow-2xl z-[9999] transform transition-transform duration-300 ease-out ${isAnimating ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="flex flex-col h-full">
              
              {/* Header */}
              <div className="flex-shrink-0 bg-[#254e70] p-6 text-[#9dacff]">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold mb-1 text-white" style={{ fontFamily: 'Kanit, sans-serif' }}>
                      Contact <span className="text-white">{therapist.firstName}</span>
                    </h3>
                    <p className="text-[#9dacff]/80 text-sm" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                      Send a secure message to start your therapy journey
                    </p>
                  </div>
                  <button
                    onClick={handleCloseModal}
                    className="text-[#9dacff]/70 hover:text-[#9dacff] transition-colors p-2 rounded-lg hover:bg-[#9dacff]/10"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-6 text-left">
                  {/* HIPAA Notice */}
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <div className="text-left">
                        <h4 className="text-sm font-semibold text-blue-800 mb-1" style={{ fontFamily: 'Kanit, sans-serif' }}>
                          Secure Communication
                        </h4>
                        <p className="text-xs text-blue-700" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                          This message will be sent securely to {therapist.firstName}. For urgent mental health concerns, please contact your local emergency services or crisis hotline immediately.
                        </p>
                      </div>
                    </div>
                  </div>

                                     <form id="contact-form" onSubmit={handleContactFormSubmit} className="space-y-5">
                                         <div>
                      <label className="block text-sm font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={handleContactFormChange}
                        required
                        minLength={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#9dacff] focus:ring-2 focus:ring-[#9dacff]/20 transition-colors"
                        placeholder="Enter your full name"
                      />
                      {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                    </div>

                     <div>
                       <label className="block text-sm font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                         Email Address *
                       </label>
                       <input
                         type="email"
                         name="email"
                         value={contactForm.email}
                         onChange={handleContactFormChange}
                         required
                         className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#9dacff] focus:ring-2 focus:ring-[#9dacff]/20 transition-colors"
                         placeholder="your.email@example.com"
                       />
                       {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                     </div>

                     <div>
                       <label className="block text-sm font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                         Phone Number (Optional)
                       </label>
                       <input
                         type="tel"
                         name="phone"
                         value={contactForm.phone}
                         onChange={handleContactFormChange}
                         className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#9dacff] focus:ring-2 focus:ring-[#9dacff]/20 transition-colors"
                         placeholder="(555) 123-4567"
                       />
                       {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                     </div>

                     <div>
                       <label className="block text-sm font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                         Preferred Contact Method
                       </label>
                       <div className="flex gap-2">
                         <button
                           type="button"
                           onClick={() => setContactForm(prev => ({ ...prev, preferredContact: 'email' }))}
                           className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all duration-200 font-medium ${
                             contactForm.preferredContact === 'email'
                               ? 'bg-[#254e70] border-[#254e70] text-white'
                               : 'bg-white border-gray-300 text-gray-700 hover:border-[#9dacff] hover:bg-[#9dacff]/5'
                           }`}
                           style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                         >
                           <div className="flex items-center justify-center gap-2">
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                             </svg>
                             Email
                           </div>
                         </button>
                         <button
                           type="button"
                           onClick={() => setContactForm(prev => ({ ...prev, preferredContact: 'phone' }))}
                           className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all duration-200 font-medium ${
                             contactForm.preferredContact === 'phone'
                               ? 'bg-[#254e70] border-[#254e70] text-white'
                               : 'bg-white border-gray-300 text-gray-700 hover:border-[#9dacff] hover:bg-[#9dacff]/5'
                           }`}
                           style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                         >
                           <div className="flex items-center justify-center gap-2">
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                             </svg>
                             Phone
                           </div>
                         </button>
                       </div>
                     </div>

                                                              <div>
                       <label className="block text-sm font-medium text-[#254e70] mb-2" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                         Message *
                       </label>
                       <textarea
                         name="message"
                         value={contactForm.message}
                         onChange={handleContactFormChange}
                         required
                         minLength={10}
                         maxLength={2000}
                         rows={5}
                         placeholder="I'm interested in therapy services. I'd like to discuss [general area of concern] and would prefer [in-person/virtual] sessions. When would be a good time to schedule a consultation?"
                         className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#9dacff] focus:ring-2 focus:ring-[#9dacff]/20 resize-none transition-colors"
                       />
                       {formErrors.message && <p className="text-red-500 text-xs mt-1">{formErrors.message}</p>}
                       <div className="flex justify-between items-center mt-1">
                         <p className="text-xs text-gray-500" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                           Keep your message general - avoid specific medical details or diagnoses.
                         </p>
                         <p className="text-xs text-gray-400" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                           {contactForm.message.length}/2000
                         </p>
                       </div>
                     </div>

                     {/* Honeypot fields - hidden from users, visible to bots */}
                     <div className="hidden" aria-hidden="true">
                       <label htmlFor="website">Website (leave blank):</label>
                       <input
                         type="text"
                         name="website"
                         id="website"
                         value={contactForm.website}
                         onChange={handleContactFormChange}
                         tabIndex="-1"
                         autoComplete="off"
                       />
                       <label htmlFor="honeypot">Comments (do not fill):</label>
                       <input
                         type="text"
                         name="honeypot"
                         id="honeypot"
                         value={contactForm.honeypot}
                         onChange={handleContactFormChange}
                         tabIndex="-1"
                         autoComplete="off"
                       />
                       <label htmlFor="comment">Additional info (leave empty):</label>
                       <input
                         type="text"
                         name="comment"
                         id="comment"
                         value={contactForm.comment}
                         onChange={handleContactFormChange}
                         tabIndex="-1"
                         autoComplete="off"
                       />
                     </div>

                     {/* Show submission delay when active */}
                     {submissionDelay > 0 && (
                       <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                         <div className="flex items-center gap-2">
                           <svg className="w-4 h-4 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                           </svg>
                           <p className="text-sm text-blue-700" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                             Processing your message... ({Math.ceil(submissionDelay / 1000)}s)
                           </p>
                         </div>
                       </div>
                     )}

                     {/* Show general form errors */}
                     {(formErrors.spam || formErrors.timing || formErrors.rateLimit) && (
                       <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                         <div className="flex items-start gap-2">
                           <svg className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                           </svg>
                           <p className="text-sm text-red-700" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                             {formErrors.spam || formErrors.timing || formErrors.rateLimit}
                           </p>
                         </div>
                       </div>
                     )}

                     {/* Terms & Conditions */}
                     <div className="border-t border-gray-200 pt-5">
                       <div className="space-y-3">
                         <div className="flex items-start gap-3">
                           <input
                             type="checkbox"
                             id="terms-privacy"
                             required
                             className="mt-1 w-4 h-4 text-[#254e70] border-gray-300 rounded focus:ring-[#9dacff]"
                           />
                           <label htmlFor="terms-privacy" className="text-xs text-gray-600" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                             I acknowledge that this initial contact is not therapy and does not establish a therapist-client relationship. I understand that {therapist.firstName} may not be accepting new clients and that insurance coverage should be verified directly.
                           </label>
                         </div>
                         
                         <div className="flex items-start gap-3">
                           <input
                             type="checkbox"
                             id="terms-communication"
                             required
                             className="mt-1 w-4 h-4 text-[#254e70] border-gray-300 rounded focus:ring-[#9dacff]"
                           />
                           <label htmlFor="terms-communication" className="text-xs text-gray-600" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                             I consent to receive communications about my inquiry via the contact method selected above. I understand that email and text messages are not completely secure forms of communication and that this is NOT a HIPAA-compliant communication method. I should NOT share detailed medical information, diagnoses, or sensitive health details in this initial contact.
                           </label>
                         </div>
                       </div>
                     </div>
                   </form>
                </div>
              </div>

              {/* Footer - Fixed */}
              <div className="flex-shrink-0 border-t border-gray-200 p-6 bg-gray-50">
                                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    disabled={isSubmitting}
                    className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="contact-form"
                    disabled={isSubmitting || submissionDelay > 0 || Object.keys(formErrors).some(key => ['spam', 'timing', 'rateLimit'].includes(key))}
                    className="flex-1 px-6 py-3 bg-[#254e70] text-white rounded-lg hover:bg-[#1d3e5a] transition-colors duration-200 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                  >
                    {isSubmitting || submissionDelay > 0 ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {submissionDelay > 0 ? `Processing... (${Math.ceil(submissionDelay / 1000)}s)` : 'Sending...'}
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Send Message
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 text-left mt-3" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                  Response time: Typically within 24 hours
                </p>
              </div>
            </div>
                                </div>
         </>,
         document.body
       )}

       {/* Success Popup */}
       {showSuccessPopup && createPortal(
         <>
           <style>
             {`
               @keyframes fadeIn {
                 from { opacity: 0; }
                 to { opacity: 1; }
               }
               
               @keyframes slideInUp {
                 from {
                   opacity: 0;
                   transform: translateY(30px) scale(0.95);
                 }
                 to {
                   opacity: 1;
                   transform: translateY(0) scale(1);
                 }
               }
               
               @keyframes iconPulse {
                 0%, 100% { transform: scale(1); }
                 50% { transform: scale(1.1); }
               }
             `}
           </style>
           <div 
             className="fixed inset-0 z-[10000] flex items-center justify-center"
             style={{
               animation: 'fadeIn 0.3s ease-out'
             }}
           >
           {/* Backdrop */}
           <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
           
           {/* Success Modal */}
           <div 
             className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 text-center"
             style={{
               animation: 'slideInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
             }}
           >
             {/* Success Icon */}
             <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ animation: 'iconPulse 1.5s ease-in-out infinite' }}>
               <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
               </svg>
             </div>
             
             {/* Success Message */}
             <h3 className="text-2xl font-bold text-[#254e70] mb-2" style={{ fontFamily: 'Kanit, sans-serif' }}>
               Message Sent! 🎉
             </h3>
             <p className="text-gray-600 mb-4" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
               Your message has been sent to <span className="font-semibold text-[#254e70]">{therapist.firstName}</span>. 
               They will respond within 24 hours.
             </p>
             
             {/* Next Steps */}
             <div className="bg-[#9dacff]/10 rounded-lg p-4 mb-6">
               <p className="text-sm text-[#254e70] font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                 💡 What happens next?
               </p>
               <ul className="text-sm text-gray-600 mt-2 space-y-1" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                 <li>• {therapist.firstName} will review your message</li>
                 <li>• They'll respond via your preferred contact method</li>
               </ul>
             </div>
             
             {/* Close Button */}
             <button
               onClick={() => setShowSuccessPopup(false)}
               className="w-full px-6 py-3 bg-[#254e70] text-white rounded-lg hover:bg-[#1d3e5a] transition-colors duration-200 font-semibold"
               style={{ fontFamily: 'Nunito Sans, sans-serif' }}
             >
               Got it! ✨
             </button>
           </div>
         </div>
         </>,
         document.body
       )}
     </>
   );
 });
 
 ContactButtons.displayName = 'ContactButtons';
 
 export default ContactButtons; 